import { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { Checkbox, Col, Divider } from 'antd'
import { CompanyTiny, Country, CompanyScope, KudosCompanyCountryVisibilityFilterInput } from '../../../types'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { CountrySelect } from './CountrySelect'
import { KUDOS_DASHBOARD_PAGE_SIZE, KUDOS_VISIBILITY_PAGE_SIZE } from '../../../graphql/kudos/util'
import { PerInfoBox } from '../../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { PerInfoBoxButtonGroupElement } from '../../../components/custom_components/InfoBox/InfoBoxButtonGroup'
import { buttonGroupButton } from '../../../components/custom_components/ButtonGroup'
import { useCompanySearch } from '../../../graphql/company/hooks/useCompanySearch'
import { CompanySearchSelect } from './CompanySearchSelect'
import CreateKudosCompanyCountryVisibilityModal from '../../../components/custom_components/CreateKudosModal/CreateKudosCompanyCountryVisibilityModal'
import { useAuth } from '../../../context/auth-context'
import { getCountriesWhereUserIsHr, getCountriesWhereUserIsHrAdmin } from '../../user/util'

interface KudosCompanyCountryVisibilityInfoBoxProps {
  filters: KudosCompanyCountryVisibilityFilterInput
  onFiltersChange: (filters: KudosCompanyCountryVisibilityFilterInput) => void
  swichContentButtons: buttonGroupButton[]
  resetFiltersAfterVisibilityChange: () => void
}

export const KudosCompanyCountryVisibilityInfoBox: FC<KudosCompanyCountryVisibilityInfoBoxProps> = ({
  filters,
  onFiltersChange,
  swichContentButtons,
  resetFiltersAfterVisibilityChange,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()

  const companySearch = useCompanySearch(CompanyScope.HrUser)
  const hrCountries = user.roles ? getCountriesWhereUserIsHr(user.roles) : []
  const hrAdminCountries = user.roles ? getCountriesWhereUserIsHrAdmin(user.roles) : []

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)

  // Handle company selection
  const handleCompanyOptionClick = useCallback(
    (c: CompanyTiny) => {
      onFiltersChange({
        ...filters,
        companyId: c.id,
        companyName: c.name,
      })
      companySearch.handleClear()
    },
    [filters, onFiltersChange, companySearch],
  )

  // Handle clearing company selection
  const handleCompanyClear = useCallback(() => {
    onFiltersChange({
      ...filters,
      companyId: undefined,
      companyName: undefined,
    })
    companySearch.handleClear()
  }, [filters, onFiltersChange, companySearch])

  // Handle country selection
  const handleCountrySelect = useCallback(
    (country: Country | null) => {
      onFiltersChange({
        ...filters,
        country: country || undefined,
      })
    },
    [filters, onFiltersChange],
  )

  const handleClearFilters = () => {
    companySearch.handleClear()
    onFiltersChange({
      status: { kudosDisabled: false, kudosEnabled: false },
      companyId: undefined,
      companyName: undefined,
      country: undefined,
      pagination: {
        offset: 0,
        limit: KUDOS_VISIBILITY_PAGE_SIZE,
      },
    })
  }

  const hasActiveFilters = () => {
    return !!(filters.companyId || filters.country || filters.status.kudosEnabled || filters.status.kudosDisabled)
  }

  return (
    <>
      <CreateKudosCompanyCountryVisibilityModal
        visible={isCreateModalVisible}
        closeModal={() => setIsCreateModalVisible(false)}
        onSuccess={() => {
          setIsCreateModalVisible(false)
        }}
        filters={{ ...filters, pagination: { offset: 0, limit: KUDOS_DASHBOARD_PAGE_SIZE } }}
        resetFiltersAfterVisibilityChange={resetFiltersAfterVisibilityChange}
      />
      <PerInfoBox>
        <Col span={17} className="fullHeight">
          <PerFlexRow direction="column" justify="space-between" className="fullHeight" width="fullWidth">
            {/* Header with title */}
            <PerFlexRow justify="space-between" align="top" width="fullWidth">
              <PerText appearance="tile-heading">{t('kudos.management.tabs.header.companyCountryVisibility')}</PerText>
              {hrAdminCountries.length > 0 && (
                <PerButtonCircle
                  icon="add_stroke"
                  color="primary"
                  variant="pastel"
                  onClick={() => setIsCreateModalVisible(true)}
                  tooltip={t('kudos.settings.createCompanyCountryVisibilityModal.title')}
                />
              )}
            </PerFlexRow>

            {/* Disclaimer */}
            <PerText fontSize="s" mb="xxxs">
              {t('kudos.management.tabs.companyCountryVisibilityDisclaimer')}
            </PerText>

            {/* Filters */}
            <PerFlexRow>
              <PerFlexRow width="fullWidth">
                {/* Company Filter */}
                <Col span={7}>
                  <PerFlexRow direction="column">
                    <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                      {t('kudos.management.filters.company')}
                    </PerText>
                    <CompanySearchSelect
                      searchTerm={companySearch.searchTerm}
                      onSearch={companySearch.handleSearch}
                      onClear={handleCompanyClear}
                      companies={companySearch.companies}
                      loading={companySearch.loading}
                      shouldShowOptions={companySearch.shouldShowOptions}
                      onOptionClick={handleCompanyOptionClick}
                      placeholder={t('kudos.management.filters.employeeCompanyPlaceholder')}
                      selectedCompany={
                        filters.companyId && filters.companyName ? { id: filters.companyId, name: filters.companyName } : null
                      }
                    />
                  </PerFlexRow>
                </Col>

                {/* Country Filter */}
                <Col span={7}>
                  <PerFlexRow direction="column">
                    <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                      {t('kudos.management.filters.country')}
                    </PerText>
                    <CountrySelect
                      countries={hrCountries}
                      selectedCountry={filters.country || null}
                      onCountrySelect={handleCountrySelect}
                      placeholder={t('kudos.management.filters.countryPlaceholder')}
                      loading={false}
                    />
                  </PerFlexRow>
                </Col>

                {/* Status Filter */}
                <Col span={7}>
                  <PerFlexRow direction="column">
                    <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxs">
                      {t('kudos.management.filters.visibility')}
                    </PerText>
                    <PerFlexRow>
                      <PerFlexRow align="middle" mr="xs">
                        <Checkbox
                          checked={filters.status.kudosEnabled}
                          onChange={(e) => {
                            onFiltersChange({
                              ...filters,
                              status: { ...filters.status, kudosEnabled: e.target.checked },
                            })
                          }}
                        />
                        <PerText fontSize="s" color="secondary" ml="xxs">
                          {t('kudos.table.enabled')}
                        </PerText>
                      </PerFlexRow>
                      <PerFlexRow align="middle">
                        <Checkbox
                          checked={filters.status.kudosDisabled}
                          onChange={(e) => {
                            onFiltersChange({
                              ...filters,
                              status: { ...filters.status, kudosDisabled: e.target.checked },
                            })
                          }}
                        />
                        <PerText fontSize="s" color="secondary" ml="xxs">
                          {t('kudos.table.disabled')}
                        </PerText>
                      </PerFlexRow>
                    </PerFlexRow>
                  </PerFlexRow>
                </Col>

                {/* Reset Filters Button */}
                <Col span={3}>
                  <PerFlexRow justify="end" align="bottom" className="fullHeight">
                    <PerButtonCircle
                      color="error"
                      icon="delete_fill"
                      variant="pastel"
                      onClick={handleClearFilters}
                      disabled={!hasActiveFilters()}
                      tooltip={t('kudos.management.filters.clear')}
                    />
                  </PerFlexRow>
                </Col>
              </PerFlexRow>
            </PerFlexRow>
          </PerFlexRow>
        </Col>

        <>
          <Divider type="vertical" className="fullHeight" />
          <Col span={6} className="fullHeight">
            <InfoBoxTile heading={t('employeeList.infoBox.switchContent')} contentFlex="fixed" headingAlign="center">
              <PerFlexRow direction="column" align="middle" justify="space-between" className="fullHeight">
                <PerText fontSize="s" color="secondary" appearance="quote" align="center">
                  {t('kudos.settings.switchContentDescription')}
                </PerText>
                <PerInfoBoxButtonGroupElement buttons={swichContentButtons} defaultValue={1} />
              </PerFlexRow>
            </InfoBoxTile>
          </Col>
        </>
      </PerInfoBox>
    </>
  )
}

export default KudosCompanyCountryVisibilityInfoBox
